import React, { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { CostAllocationConfig } from '@/components/admin/CostAllocationConfig';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  buildDailyNervSourceOptions,
  buildReportSourceOptions,
  resolveDailyNervSources,
  resolveReportSources,
  selectableReportSources,
  type ResolvedOrigin,
} from '@/shared/reportDataSources';
import {
  parseReportDataSources,
  REPORT_SOURCE_LABELS,
  reportSourceChoiceIds,
  toReportSourceChoice,
  type ReportSourceChoice,
  type ReportSourceKey,
} from '@/shared/types/reportDataSources';
import { saveReportDataSource } from '@/utils/organization/saveReportDataSource';
import { EditButton } from './EditButton';
import { InventorySourceRow } from './InventorySourceRow';
import { SettingsRow, SettingsSection } from './SettingsGroup';
import { SourceMultiSelect } from './SourceMultiSelect';
import { missingSourceNote } from '../reports/dataSourceNote';

// Reports and Charts › Data sources (FIG-345 item 6 · UC-229, REQ-031, REQ-032).
// Each row saves the sources a report or chart uses for this merchant, for every
// admin viewing it. The report and chart no longer offer their own selectors.
// Fixed Costs keeps its Cost Allocation, unchanged.
// FIG-409 · UC-233: each row is a checkbox multi-select (one, several or all
// sources); the ticked set is saved canonically (toReportSourceChoice).
// FIG-442 · UC-251: the first row is the Inventory source (one source only).
// FIG-238 · UC-257 (D6): the Daily NERV Trends row is read-only, "Uses Variant History snapshot
// data", with no Edit control. The chart plots stored Variant History values, so its own source
// picker is HIDDEN, not deleted: the picker code below is kept for the key, and the saved
// selection (organisations.report_data_sources.dailyNervTrends) stays in the database, ignored,
// so the row can be brought back later. An admin changes the Variants data source to change
// where Variant History's figures come from.

interface ReportsDataSourcesSectionProps {
  organisationId: string;
  /** Connection details for Cost Allocation (unchanged). */
  connections: unknown[];
  /** organisations.connections: what the report and chart choose from. */
  merchantConnections: unknown;
  /** organisations.report_data_sources. */
  reportDataSources: unknown;
  /** organisations.inventory_source_config (FIG-442): the Inventory source row. */
  inventorySourceConfig?: unknown;
  costAllocationConfig: unknown;
  canEdit: boolean;
  onUpdated: () => void;
}

interface RowOption {
  value: string;
  label: string;
}

interface CurrentSelection {
  label: string;
  origin: ResolvedOrigin;
  /** The option values in effect now (all of them for "All Sources"). */
  values: string[];
  missingCount: number;
}

const ROW_DESCRIPTIONS: Record<ReportSourceKey, string> = {
  variants: 'Used by the ∑: Variants report (and Fixed Costs, which shares it).',
  salesHistory: 'Used by the Sales History report.',
  dailyNervTrends: 'Used by the Daily NERV Trends chart on the Overview tab. To change where its figures come from, change the Variants data source: Variant History is built from it.',
};

/** What the Daily NERV Trends row shows instead of a source (FIG-238). */
export const DAILY_NERV_SNAPSHOT_VALUE = 'Uses Variant History snapshot data';

const originNote = (origin: ResolvedOrigin, missingCount: number): string | null => {
  if (origin === 'default') return 'Not set: the report picks its default (API connections over CSV imports).';
  return missingSourceNote(origin, missingCount);
};

const sameChoice = (a: ReportSourceChoice | null, b: ReportSourceChoice | undefined): boolean => {
  if (!a || !b || a.kind !== b.kind) return false;
  const aIds = reportSourceChoiceIds(a);
  const bIds = reportSourceChoiceIds(b);
  return aIds.length === bIds.length && aIds.every((id) => bIds.includes(id));
};

export const ReportsDataSourcesSection: React.FC<ReportsDataSourcesSectionProps> = ({
  organisationId,
  connections,
  merchantConnections,
  reportDataSources,
  inventorySourceConfig,
  costAllocationConfig,
  canEdit,
  onUpdated,
}) => {
  const saved = useMemo(() => parseReportDataSources(reportDataSources), [reportDataSources]);
  const reportOptions = useMemo(() => buildReportSourceOptions(merchantConnections), [merchantConnections]);
  const chartOptions = useMemo(() => buildDailyNervSourceOptions(merchantConnections), [merchantConnections]);

  // The sources a row offers (no "All" entry: "Select all" covers it).
  const rowOptions = (key: ReportSourceKey): RowOption[] => {
    if (key === 'dailyNervTrends') {
      return chartOptions.filter((o) => o.value !== 'All');
    }
    return selectableReportSources(reportOptions).map((s) => ({ value: s.id, label: s.name }));
  };

  const current = (key: ReportSourceKey): CurrentSelection => {
    if (key === 'dailyNervTrends') {
      const resolved = resolveDailyNervSources(saved[key], merchantConnections);
      return {
        label: resolved.label,
        origin: resolved.origin,
        // [] means All Sources (no filter): every offered value is in effect.
        values: resolved.values.length > 0 ? resolved.values : rowOptions(key).map((o) => o.value),
        missingCount: resolved.missingCount ?? 0,
      };
    }
    const resolved = resolveReportSources(saved[key], reportOptions);
    return { label: resolved.label, origin: resolved.origin, values: resolved.ids, missingCount: resolved.missingCount ?? 0 };
  };

  // One row open at a time (REQ-024).
  const [editingKey, setEditingKey] = useState<ReportSourceKey | null>(null);
  const [draft, setDraft] = useState<string[]>([]);
  // True once the admin changes a checkbox. Until then the saved choice is kept
  // as is, so an explicit multi-source selection that happens to match every
  // source still connected is never re-saved as "All" (Cursor Bugbot on #360).
  const [draftTouched, setDraftTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ key: ReportSourceKey; message: string; kind: 'saved' | 'error' } | null>(null);

  useEffect(() => {
    if (status?.kind !== 'saved') return;
    const timer = setTimeout(() => setStatus(null), 4000);
    return () => clearTimeout(timer);
  }, [status]);

  // Losing edit rights (admin mode turned off) drops any open edit, even if the
  // section isn't remounted (Greptile P2 on #286).
  useEffect(() => {
    if (!canEdit) setEditingKey(null);
  }, [canEdit]);

  // The draft starts from what the report or chart uses now.
  const startEditing = (key: ReportSourceKey) => {
    setDraft(current(key).values);
    setDraftTouched(false);
    setStatus(null);
    setEditingKey(key);
  };

  const changeDraft = (next: string[]) => {
    setDraft(next);
    setDraftTouched(true);
  };

  // Untouched: the saved choice itself, or nothing when the row is unset, so
  // Save stays disabled until a checkbox changes. Opening an unset row never
  // freezes the dynamic smart default (API over CSV) into a saved value (Cursor
  // review on #360). Changed: the ticked set, canonicalised, so ticking every
  // source or "Select all" saves "All" (AC4).
  const draftChoice = (key: ReportSourceKey): ReportSourceChoice | null => {
    if (!draftTouched) return saved[key] ?? null;
    return toReportSourceChoice(draft, rowOptions(key).map((o) => o.value));
  };

  const save = async (key: ReportSourceKey) => {
    const choice = draftChoice(key);
    if (!choice || !canEdit) return;
    setSaving(true);
    const result = await saveReportDataSource(organisationId, key, choice);
    setSaving(false);
    if (!result.ok) {
      setStatus({ key, kind: 'error', message: result.error ?? 'Could not save the data source' });
      return;
    }
    // Close only the row that was saved (Greptile P2 on #286).
    setEditingKey((open) => (open === key ? null : open));
    setStatus({ key, kind: 'saved', message: result.recorded ? 'Saved · recorded in history' : 'Saved, but not recorded in history' });
    onUpdated();
  };

  const renderRow = (key: ReportSourceKey) => {
    // FIG-238: read-only, no Edit control, nothing is saved from this row.
    if (key === 'dailyNervTrends') {
      return (
        <SettingsRow key={key} label={REPORT_SOURCE_LABELS[key]} editing={false} testId={`report-source-${key}`} action={null}>
          <div className="font-medium">{DAILY_NERV_SNAPSHOT_VALUE}</div>
          <div className="mt-1 text-sm text-muted-foreground">{ROW_DESCRIPTIONS[key]}</div>
        </SettingsRow>
      );
    }
    const options = rowOptions(key);
    const { label, origin, missingCount } = current(key);
    const editing = canEdit && editingKey === key;
    const note = originNote(origin, missingCount);
    const rowStatus = status?.key === key ? status : null;
    const noSources = options.length === 0;
    // AC8: with a single source there is nothing to choose.
    const choosable = options.length > 1;
    const pending = editing ? draftChoice(key) : null;

    return (
      <SettingsRow
        key={key}
        label={REPORT_SOURCE_LABELS[key]}
        editing={editing}
        testId={`report-source-${key}`}
        // No other row can be opened while a save is running.
        action={canEdit && !editing && choosable && !saving ? (
          <EditButton label={`Edit ${REPORT_SOURCE_LABELS[key]}`} onClick={() => startEditing(key)} />
        ) : null}
      >
        <div className="flex items-center gap-2">
          <span className="font-medium">{noSources ? 'No connected sources' : label}</span>
          {rowStatus?.kind === 'saved' && <span role="status" className="text-sm text-emerald-700">{rowStatus.message}</span>}
        </div>
        <div className="mt-1 text-sm text-muted-foreground">{ROW_DESCRIPTIONS[key]}</div>
        {note && !noSources && <div className="mt-1 text-xs text-muted-foreground">{note}</div>}
        {editing && (
          <div className="mt-3 space-y-3">
            <div className="max-w-[320px] space-y-1.5">
              <Label htmlFor={`report-source-${key}-select`}>{REPORT_SOURCE_LABELS[key]}</Label>
              <SourceMultiSelect
                id={`report-source-${key}-select`}
                label={REPORT_SOURCE_LABELS[key]}
                options={options}
                selected={draft}
                onChange={changeDraft}
                summary={draftTouched ? undefined : label}
                disabled={saving}
              />
            </div>
            {rowStatus?.kind === 'error' && (
              <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
                <strong>Data source not saved.</strong> {rowStatus.message}. The previous source was kept.
              </div>
            )}
            <div className="flex gap-2">
              <Button type="button" onClick={() => save(key)} disabled={saving || !pending || sameChoice(pending, saved[key])}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save source
              </Button>
              <Button type="button" variant="outline" onClick={() => setEditingKey(null)} disabled={saving}>Cancel</Button>
            </div>
          </div>
        )}
      </SettingsRow>
    );
  };

  return (
    <SettingsSection
      title="Data Sources"
      description="Which data source each report uses for this merchant, and whose stock counts as Stock Held. Saved here for every admin; the reports and Inventory tab show it but no longer change it. The Daily NERV Trends chart reads Variant History."
    >
      <InventorySourceRow
        organisationId={organisationId}
        merchantConnections={merchantConnections}
        inventorySourceConfig={inventorySourceConfig}
        canEdit={canEdit}
        onUpdated={onUpdated}
      />
      {renderRow('variants')}
      {renderRow('salesHistory')}
      {renderRow('dailyNervTrends')}
      <div className="border-t pt-4" data-testid="fixed-costs-data-source">
        <h4 className="mb-1 font-medium">Fixed Costs data source · Cost Allocation</h4>
        <CostAllocationConfig
          organisationId={organisationId}
          connections={connections}
          currentConfig={costAllocationConfig as React.ComponentProps<typeof CostAllocationConfig>['currentConfig']}
          onConfigUpdate={onUpdated}
          readOnly={!canEdit}
        />
      </div>
    </SettingsSection>
  );
};

export default ReportsDataSourcesSection;
